import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Observable, of, Subject } from 'rxjs';
import { catchError, map, takeUntil, tap } from 'rxjs/operators';
import { VoiceRecordingService, VoiceResult, VoiceStartOptions } from '../services/voice-recording.service';

export type VoiceRecordingResult = VoiceResult;

export enum RecorderState { Idle = 'Idle', Recording = 'Recording' }

export interface VoiceRecordingStartOptions extends VoiceStartOptions {
  maxDurationSec?: number;
}

type Stop$ = () => Observable<VoiceResult>;

@Injectable({ providedIn: 'root' })
export class VoiceRecordingFacade {
  private state$ = new BehaviorSubject<RecorderState>(RecorderState.Idle);
  private elapsedMs$ = new BehaviorSubject<number>(0);

  private controller?: { stop$: Stop$ };
  private timerStop$ = new Subject<void>();
  private startedAt = 0;
  private maxDurationSec?: number;
  private lastResult?: VoiceRecordingResult;

  constructor(private srv: VoiceRecordingService) {
  }

  get formattedTime(): string {
    const total = Math.round(this.elapsedMs$.value / 1000);
    const mm = Math.floor(total / 60).toString().padStart(2, '0');
    const ss = (total % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
  }

  startRecordingVoice$(opts: VoiceRecordingStartOptions = {}): Observable<void> {
    if (this.state$.value === RecorderState.Recording) return of(void 0);
    this.maxDurationSec = opts?.maxDurationSec;

    return this.srv.start$({ format: opts?.format }).pipe(
      tap(ctrl => {
        this.controller = ctrl;
        this.startedAt = performance.now();
        this.elapsedMs$.next(0);
        this.state$.next(RecorderState.Recording);

        this.timerStop$.next();

        interval(200).pipe(takeUntil(this.timerStop$)).subscribe(() => {
          const ms = performance.now() - this.startedAt;
          this.elapsedMs$.next(ms);

          if (this.maxDurationSec && ms >= this.maxDurationSec * 1000) {
            this.elapsedMs$.next(this.maxDurationSec * 1000);
            this.timerStop$.next();
            this.stopRecordingVoice$({ preserveElapsed: true }).subscribe({
              error: () => {
              },
            });
          }
        });
      }),
      map(() => void 0),
      catchError(err => {
        throw err || 'failed_to_start_recording';
      }),
    );
  }

  stopRecordingVoice$(opts?: { preserveElapsed?: boolean }): Observable<VoiceRecordingResult> {
    if (!this.controller) {
      if (this.lastResult) {
        const res = this.lastResult;
        this.lastResult = undefined;
        return of(res);
      }

      return new Observable<VoiceRecordingResult>(obs => obs.error('failed_to_stop_recording'));
    }

    const stop$ = this.controller.stop$;
    this.controller = undefined;
    this.timerStop$.next();

    return stop$().pipe(
      tap(res => {
        this.lastResult = res;
      }),
      tap(() => this.resetState(!opts?.preserveElapsed)),
      catchError(err => {
        this.resetState();
        throw err || 'failed_to_stop_recording';
      }),
    );
  }

  cancelRecordingVoice(): void {
    try {
      this.srv.cancel();
    } catch {
    }
    this.controller = undefined;
    this.timerStop$.next();
    this.lastResult = undefined;
    this.resetState();
  }

  private resetState(resetElapsed = true) {
    this.state$.next(RecorderState.Idle);
    if (resetElapsed) this.elapsedMs$.next(0);
    this.maxDurationSec = undefined;
  }
}
